export interface StoredAuthSession {
  accessToken: string;
  refreshToken: string | null;
}

type AuthStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

const ACCESS_TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";

let refreshInFlight: Promise<string | null> | null = null;

export function readStoredAuthSession(
  storage: AuthStorage = window.localStorage,
): StoredAuthSession | null {
  const accessToken = storage.getItem(ACCESS_TOKEN_KEY);
  if (!accessToken) return null;

  return {
    accessToken,
    refreshToken: storage.getItem(REFRESH_TOKEN_KEY),
  };
}

export function saveAuthSession(
  session: StoredAuthSession,
  storage: AuthStorage = window.localStorage,
): void {
  storage.setItem(ACCESS_TOKEN_KEY, session.accessToken);

  if (session.refreshToken) {
    storage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
  } else {
    storage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function clearAuthSession(
  storage: AuthStorage = window.localStorage,
): void {
  storage.removeItem(ACCESS_TOKEN_KEY);
  storage.removeItem(REFRESH_TOKEN_KEY);
}

export async function refreshStoredAuthSession(
  apiBase: string,
  storage: AuthStorage = window.localStorage,
  fetchFn: FetchLike = fetch,
): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = refreshStoredAuthSessionOnce(apiBase, storage, fetchFn)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

async function refreshStoredAuthSessionOnce(
  apiBase: string,
  storage: AuthStorage,
  fetchFn: FetchLike,
): Promise<string | null> {
  const session = readStoredAuthSession(storage);
  if (!session?.refreshToken) return null;

  const res = await fetchFn(`${apiBase}/api/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: session.refreshToken }),
  });
  const json = (await res.json().catch(() => null)) as {
    ok?: boolean;
    data?: { accessToken?: string; refreshToken?: string };
  } | null;

  const accessToken = json?.data?.accessToken;
  const refreshToken = json?.data?.refreshToken;
  if (!res.ok || !json?.ok || !accessToken || !refreshToken) {
    clearAuthSession(storage);
    return null;
  }

  saveAuthSession({ accessToken, refreshToken }, storage);
  return accessToken;
}

export async function logoutStoredAuthSession(
  apiBase: string,
  storage: AuthStorage = window.localStorage,
  fetchFn: FetchLike = fetch,
): Promise<void> {
  const refreshToken = storage.getItem(REFRESH_TOKEN_KEY);
  clearAuthSession(storage);

  if (!refreshToken) return;

  await fetchFn(`${apiBase}/api/auth/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  }).catch(() => undefined);
}
